import { log as fullStoryLog, isInitialized as isFullStoryInitialized } from '@fullstory/browser';

import { fullStoryTracking, type FullStoryEvent } from '../fullstory/tracking';
import { newRelic } from '../newRelic/newRelic';
import { FULLSTORY_CONFIG } from './config';
import { type ObservabilityEvent } from './events';

const DEFAULT_TIMEOUT_MS = 60000;

type ObservabilityEventWithId = `${ObservabilityEvent}_${string}` | ObservabilityEvent;
type ErrorSeverityLevels = 'CRITICAL' | 'ERROR' | 'WARNING';
type ObservabilityEventTypes = 'SUCCESS' | 'TIMEOUT' | ErrorSeverityLevels;
type ObservabilityEventWithIdTypes = `${ObservabilityEvent}_${ObservabilityEventTypes}`;

type Event = ObservabilityEvent | ObservabilityEventWithIdTypes;

type ObservabilityAttributes = {
  force?: boolean;
  id?: string;
} & Record<string, unknown>;
type ObservabilityStartAttributes = {
  id?: string;
  preservedAttributes?: Record<string, unknown>;
  timeoutMs?: number;
  value?: string;
};
type ObservabilityCancelAttributes = {
  id?: string;
};

class ObservabilityClient {
  private pendingEvents: Partial<
    Record<
      ObservabilityEventWithId,
      { preservedAttributes?: Record<string, unknown>; startTime: number; timeoutId: number }
    >
  > = {};

  private clearEvent(name: ObservabilityEvent, id?: string) {
    const pendingEventName = this.getEventName(name, id);
    const event = this.pendingEvents[pendingEventName];
    if (!event) return;

    clearTimeout(event.timeoutId);
    delete this.pendingEvents[pendingEventName];
  }

  private getEventName(name: ObservabilityEvent, id?: string) {
    if (id) return `${name}_${id}` as ObservabilityEventWithId;
    return name as ObservabilityEvent;
  }

  private sendFullStoryEvent(name: Event, attributes?: ObservabilityAttributes) {
    try {
      if (!isFullStoryInitialized()) return;
      let properties = attributes || {};
      if (FULLSTORY_CONFIG[name]) {
        if (!FULLSTORY_CONFIG[name]?.enabled) return;
        if (!FULLSTORY_CONFIG[name]?.sendProperties) properties = {};
      }
      fullStoryTracking(name as FullStoryEvent, properties as Record<string, never>);
    } catch (e) {
      // noop
    }
  }

  private sendFullStoryLog(name: Event, attributes?: ObservabilityAttributes) {
    try {
      if (!isFullStoryInitialized()) return;
      if (attributes) {
        fullStoryLog(
          'info',
          `[Observability event] ${name} with attributes: ${JSON.stringify(attributes)}`,
        );
      } else {
        fullStoryLog('info', `[Observability event] ${name}`);
      }
    } catch (e) {
      // noop
    }
  }

  private sendPageAction(name: Event, attributes?: ObservabilityAttributes) {
    try {
      newRelic.addPageAction(name, attributes);
    } catch (e) {
      // noop
    }
  }

  private timeoutEvent(name: ObservabilityEvent, id?: string, timeoutMs = DEFAULT_TIMEOUT_MS) {
    this.clearEvent(name, id);

    const timeoutEventName = `${name}_TIMEOUT` as const;
    this.sendPageAction(timeoutEventName, { id, timeout: timeoutMs });
    this.sendFullStoryLog(timeoutEventName, { id });
    this.sendFullStoryEvent(timeoutEventName, { id, timeout: timeoutMs });
  }

  cancelEvent(name: ObservabilityEvent, attributes?: ObservabilityCancelAttributes) {
    const pendingEventName = this.getEventName(name, attributes?.id);
    if (!this.pendingEvents[pendingEventName]) return;
    this.clearEvent(name, attributes?.id);
    this.sendFullStoryLog(name, { ...attributes, cancelled: true });
  }

  errorEvent(
    name: ObservabilityEvent,
    attributes?: ObservabilityAttributes,
    severity: ErrorSeverityLevels = 'ERROR',
  ) {
    const pendingEventName = this.getEventName(name, attributes?.id);
    const event = this.pendingEvents[pendingEventName];
    const force = attributes?.force;
    if (!force && !event) return;

    const preservedAttributes = event?.preservedAttributes || {};

    const duration = Date.now() - (event?.startTime || 0);
    this.clearEvent(name, attributes?.id);

    const errorEventName = `${name}_${severity}` as const;
    this.sendPageAction(errorEventName, { ...preservedAttributes, duration, ...attributes });
    this.sendFullStoryLog(errorEventName, { ...preservedAttributes, ...attributes });
    this.sendFullStoryEvent(errorEventName, { ...preservedAttributes, duration, ...attributes });
  }

  singleEvent(name: ObservabilityEvent, attributes?: ObservabilityAttributes) {
    this.sendPageAction(name, attributes);
    this.sendFullStoryLog(name, attributes);
    this.sendFullStoryEvent(name, attributes);
  }

  startEvent(name: ObservabilityEvent, attributes?: ObservabilityStartAttributes) {
    this.clearEvent(name, attributes?.id);
    const timeoutId = window.setTimeout(
      () => this.timeoutEvent(name, attributes?.id, attributes?.timeoutMs),
      attributes?.timeoutMs || DEFAULT_TIMEOUT_MS,
    );
    const pendingEventName = this.getEventName(name, attributes?.id);
    this.pendingEvents[pendingEventName] = {
      preservedAttributes: attributes?.preservedAttributes || {},
      startTime: Date.now(),
      timeoutId,
    };
    this.sendPageAction(name);
    this.sendFullStoryLog(name, attributes);
  }

  successEvent(name: ObservabilityEvent, attributes?: ObservabilityAttributes) {
    const pendingEventName = this.getEventName(name, attributes?.id);
    const event = this.pendingEvents[pendingEventName];
    const force = attributes?.force;
    if (!force && !event) return;

    const preservedAttributes = event?.preservedAttributes || {};

    const duration = Date.now() - (event?.startTime || 0);
    this.clearEvent(name, attributes?.id);

    const successEventName = `${name}_SUCCESS` as const;
    this.sendPageAction(successEventName, { duration, ...preservedAttributes, ...attributes });
    this.sendFullStoryLog(successEventName, { ...preservedAttributes, ...attributes });
    this.sendFullStoryEvent(successEventName, { duration, ...preservedAttributes, ...attributes });
  }
}

export const observability = new ObservabilityClient();
